import { faArrowUpRightAndArrowDownLeftFromCenter } from '@fortawesome/pro-light-svg-icons';
import {
  ColorType,
  CrosshairMode,
  DeepPartial,
  ChartOptions,
  IChartApi,
  ISeriesApi,
  LineData,
  UTCTimestamp,
  createChart,
} from 'lightweight-charts';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Widget } from '../../../../../components/v2/widget';
import { useThemeMode } from '../../../../../providers/theme-provider';
import type { ResolvedThemeMode } from '../../../../../theme/theme-store';
import { getTokenValue } from '../../../../../styles/tokens';
import { getLineChartColors } from '../../../../../theme/colors';

import { formatTickMark } from './helpers';

const getChartThemeOptions = (mode: ResolvedThemeMode): DeepPartial<ChartOptions> => {
  const axisBorderColor = getTokenValue('border.chartGrid', mode);

  return {
    layout: {
      textColor: getTokenValue('text.chartAxis', mode),
    },
    grid: {
      vertLines: { color: axisBorderColor },
      horzLines: { color: axisBorderColor },
    },
    timeScale: {
      borderColor: axisBorderColor,
    },
    rightPriceScale: {
      borderColor: axisBorderColor,
    },
  };
};

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Date + time (these snapshots are intraday), matching the crosshair-tooltip
// date style used by PortfolioPerformanceChartType / the Performance card so
// hovering feels identical across the app.
const fmtTooltipDate = (time: UTCTimestamp): string => {
  const d = new Date((time as number) * 1000);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()} ${hh}:${mm}`;
};

interface TooltipRow {
  label: string;
  value: number;
  color: string;
}

interface TooltipState {
  x: number;
  y: number;
  date: string;
  rows: TooltipRow[];
}

type MarketDataSeriesConfig = {
  key: string;
  label: string;
  data: LineData[];
};

type MarketDataSeriesWithColor = MarketDataSeriesConfig & {
  color: string;
};

interface MarketDataChartProps {
  cardKey: string;
  title: string;
  seriesConfigs: MarketDataSeriesConfig[];
  maximumFractionDigits: number;
  minMove: number;
  // Appended to axis / crosshair / tooltip values (e.g. "%"). Callers that
  // pass this are responsible for pre-scaling their data (e.g. ATM vols ×100),
  // since this is display-only.
  valueSuffix?: string;
}

const getInitialVisibility = (seriesConfigs: MarketDataSeriesConfig[]) =>
  Object.fromEntries(seriesConfigs.map(({ key }) => [key, true]));

export const MarketDataChart = memo(
  ({
    cardKey,
    title,
    seriesConfigs,
    maximumFractionDigits,
    minMove,
    valueSuffix = '',
  }: MarketDataChartProps) => {
    const { resolvedMode } = useThemeMode();
    const isDark = resolvedMode === 'dark';
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | undefined>(undefined);
    const seriesRefs = useRef<Record<string, ISeriesApi<'Line'>>>({});
    // Read inside the crosshair handler (set up once) so the tooltip always
    // reflects current labels/colours/visibility without re-subscribing.
    const seriesMetaRef = useRef<
      Record<string, { label: string; color: string; visible: boolean }>
    >({});
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);
    const [visibility, setVisibility] = useState<Record<string, boolean>>(() =>
      getInitialVisibility(seriesConfigs),
    );
    const seriesKeySignature = useMemo(
      () => seriesConfigs.map(({ key }) => key).join('\0'),
      [seriesConfigs],
    );

    const seriesWithColors: MarketDataSeriesWithColor[] = useMemo(() => {
      const palette = getLineChartColors(resolvedMode);
      return seriesConfigs.map((config, index) => ({
        ...config,
        color: palette[index % palette.length],
      }));
    }, [seriesConfigs, resolvedMode]);

    const hasChartData = useMemo(
      () => seriesConfigs.some(({ data }) => data.length > 0),
      [seriesConfigs],
    );

    useEffect(() => {
      setVisibility(getInitialVisibility(seriesConfigs));
    }, [seriesConfigs]);

    useEffect(() => {
      seriesMetaRef.current = Object.fromEntries(
        seriesWithColors.map(({ key, label, color }) => [
          key,
          { label, color, visible: visibility[key] ?? true },
        ]),
      );
    }, [seriesWithColors, visibility]);

    useEffect(() => {
      if (!containerRef.current) return;

      const themeOptions = getChartThemeOptions(resolvedMode);
      const chart = createChart(containerRef.current, {
        autoSize: true,
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: themeOptions.layout?.textColor,
        },
        grid: themeOptions.grid,
        crosshair: { mode: CrosshairMode.Normal },
        timeScale: {
          borderColor: themeOptions.timeScale?.borderColor,
          timeVisible: false,
          visible: true,
          barSpacing: 20,
          tickMarkFormatter: formatTickMark,
        },
        rightPriceScale: {
          autoScale: true,
          borderColor: themeOptions.rightPriceScale?.borderColor,
          alignLabels: true,
          entireTextOnly: true,
          scaleMargins: { top: 0.1, bottom: 0.1 },
        },
        leftPriceScale: {
          visible: false,
        },
        localization: {
          priceFormatter: (price: number) =>
            `${price.toLocaleString('en-US', { maximumFractionDigits })}${valueSuffix}`,
        },
      });

      chartRef.current = chart;

      chart.subscribeCrosshairMove((param) => {
        if (!param.point || !param.time) {
          setTooltip(null);
          return;
        }
        const rows: TooltipRow[] = [];
        for (const [key, series] of Object.entries(seriesRefs.current)) {
          const meta = seriesMetaRef.current[key];
          if (meta && !meta.visible) continue;
          const point = param.seriesData.get(series) as { value?: number } | undefined;
          if (!point || typeof point.value !== 'number') continue;
          rows.push({
            label: meta?.label ?? key,
            value: point.value,
            color: meta?.color ?? '#888',
          });
        }
        if (rows.length === 0) {
          setTooltip(null);
          return;
        }
        setTooltip({
          x: param.point.x,
          y: param.point.y,
          date: fmtTooltipDate(param.time as UTCTimestamp),
          rows,
        });
      });

      const palette = getLineChartColors(resolvedMode);
      seriesConfigs.forEach(({ key }, index) => {
        const series = chart.addLineSeries({
          color: palette[index % palette.length],
          lineWidth: 2,
          lastValueVisible: false,
          priceLineVisible: false,
          priceFormat: {
            type: 'price',
            precision: maximumFractionDigits,
            minMove,
          },
        });
        seriesRefs.current[key] = series;
      });

      return () => {
        chart.remove();
        chartRef.current = undefined;
        seriesRefs.current = {};
      };
    }, [maximumFractionDigits, minMove, seriesKeySignature, valueSuffix]);

    useEffect(() => {
      chartRef.current?.applyOptions(getChartThemeOptions(resolvedMode));
    }, [resolvedMode]);

    useEffect(() => {
      const palette = getLineChartColors(resolvedMode);
      seriesConfigs.forEach(({ key }, index) => {
        seriesRefs.current[key]?.applyOptions({ color: palette[index % palette.length] });
      });
    }, [resolvedMode, seriesKeySignature]);

    useEffect(() => {
      seriesConfigs.forEach(({ key, data }) => {
        const series = seriesRefs.current[key];
        if (!series) return;
        series.setData(data);
      });

      if (hasChartData) {
        chartRef.current?.timeScale().fitContent();
      }
    }, [hasChartData, seriesConfigs]);

    const toggleSeries = useCallback(
      (key: string) => {
        if (visibility[key] && Object.values(visibility).filter(Boolean).length === 1) {
          return;
        }

        const nextVisibility = !visibility[key];
        setVisibility((prev) => ({ ...prev, [key]: nextVisibility }));
        seriesRefs.current[key]?.applyOptions({ visible: nextVisibility });
      },
      [visibility],
    );

    return (
      <Widget cardKey={cardKey}>
        <Widget.Header>
          <Widget.Title title={title} />
          <Widget.Buttons>
            <Widget.Button icon={faArrowUpRightAndArrowDownLeftFromCenter} onClick={() => {}} />
          </Widget.Buttons>
        </Widget.Header>
        <Widget.Content>
          <div className="market-data-chart-inner">
            <div className={`market-data-chart-watermark watermark watermark-${resolvedMode}`} />
            <div ref={containerRef} className="market-data-chart-container">
              {tooltip && (
                <div
                  className="market-data-chart-tooltip"
                  style={{
                    left: Math.min(tooltip.x + 12, (containerRef.current?.clientWidth ?? 0) - 150),
                    top: Math.max(tooltip.y - 12 - tooltip.rows.length * 16, 4),
                    backgroundColor: isDark ? '#0d1f2a' : '#ffffff',
                    border: `1px solid ${isDark ? '#1f3645' : 'rgba(9, 45, 58, 0.15)'}`,
                    color: isDark ? '#e8f4fd' : '#092D3A',
                  }}
                >
                  <div
                    className="market-data-chart-tooltip-date"
                    style={{ color: isDark ? 'rgba(232, 244, 253, 0.6)' : 'rgba(9, 45, 58, 0.6)' }}
                  >
                    {tooltip.date}
                  </div>
                  {tooltip.rows.map((row) => (
                    <div key={row.label} className="market-data-chart-tooltip-row">
                      <span
                        className="market-data-chart-tooltip-dot"
                        style={{ background: row.color }}
                      />
                      <span className="market-data-chart-tooltip-label">{row.label}</span>
                      <span className="market-data-chart-tooltip-value">
                        {`${row.value.toLocaleString('en-US', { maximumFractionDigits })}${valueSuffix}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="market-data-chart-legend">
              {seriesWithColors.map(({ key, label, color }) => (
                <button
                  key={key}
                  className={`market-data-chart-legend-item ${visibility[key] ? 'active' : 'inactive'}`}
                  onClick={() => toggleSeries(key)}
                >
                  <span className="market-data-chart-legend-dot" style={{ background: color }} />
                  <span className="market-data-chart-legend-label">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </Widget.Content>
        <Widget.Footer />
      </Widget>
    );
  },
);

MarketDataChart.displayName = 'MarketDataChart';
